import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountsService } from '../services/accounts.service';
import { ChannelsService } from '../services/channels.service';
import { AlertService } from '../services/alert.service';
import { Option } from '../pages/accounts/accounts-channels-configurator.partial';

export interface RcsAgents {
  CONVERSATIONAL: Option[];
  SINGLE: Option[];
  BASIC: Option[];
}

const useChannelData = (companyId: string) => {
  const [waAccounts, setWaAccounts] = useState<Option[]>([]);
  const [rcsAgents, setRcsAgents] = useState<RcsAgents>({
    CONVERSATIONAL: [],
    SINGLE: [],
    BASIC: []
  });
  const [smsOutputs, setSmsOutputs] = useState<Option[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const fetchWaAccounts = useCallback(async () => {
    try {
      const response = await AccountsService.getWaAccountsForCompany(companyId);
      const activeAccounts = response.filter((item: any) => item.status === true);
      const formattedData = activeAccounts.map((item: any) => ({
        value: item._id.toString(),
        label: item.name
      }));
      setWaAccounts(formattedData);
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  }, [companyId, t]);

  const fetchEmailAccounts = useCallback(async () => {
    try {
      const response = await ChannelsService.getMailingAccounts();
      const formattedData = response.map((item: any) => ({
        value: item._id.toString(),
        label: item.name
      }));
      setEmailAccounts(formattedData);
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  }, [t]);

  const fetchRcsAgents = useCallback(async () => {
    try {
      const response = await AccountsService.getRCSAccountsForCompany(companyId);
      const groupedAgents: RcsAgents = {
        CONVERSATIONAL: [],
        SINGLE: [],
        BASIC: []
      };

      response.forEach((agent: any) => {
        const type = agent?.type?.toUpperCase();
        if (['BASIC', 'SINGLE', 'CONVERSATIONAL'].includes(type)) {
          groupedAgents[type as keyof RcsAgents].push({
            value: agent._id,
            label: agent.name
          });
        }
      });

      setRcsAgents(groupedAgents);
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  }, [companyId, t]);

  const fetchSmsOutputs = useCallback(async () => {
    try {
      const response = await ChannelsService.getMasksSMS();
      const activeOutputs = response.filter((item: any) => item.status === true);
      const formattedData = activeOutputs.map((item: any) => ({
        value: item._id,
        label: item.name
      }));
      setSmsOutputs(formattedData);
    } catch (error: any) {
      AlertService.toastError(t('general.atention'), error);
    }
  }, [t]);

  const fetchAllData = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      await Promise.all([
        fetchWaAccounts(),
        fetchRcsAgents(),
        fetchSmsOutputs(),
        fetchEmailAccounts()
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, fetchWaAccounts, fetchRcsAgents, fetchSmsOutputs, fetchEmailAccounts]);

  return {
    waAccounts,
    rcsAgents,
    smsOutputs,
    emailAccounts,
    isLoading,
    fetchAllData
  };
};
export default useChannelData;
